/**
 * Meu Padrão — agenda operacional do dia.
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  LST_FREQUENCY_LABELS,
  LST_CATEGORY_STYLES,
  type LstCategory,
  type LstRotinaDoDia,
  type LstStatus,
} from '../types';
import { formatTimeHHmm } from '../services/lstMatrixHourly';
import { formatDesvioJustificativa } from '../utils/kamishibaiStatus';
import { meuPadraoIsLate, meuPadraoRowId, meuPadraoSortKey } from '../utils/meuPadraoDayUtils';
import { MeuPadraoAgendaBadge } from './meu-padrao/MeuPadraoAgendaBadge';

export interface MeuPadraoPanelProps {
  rotinas: LstRotinaDoDia[];
  busyTemplateId: string | null;
  statusById: ReadonlyMap<string, LstStatus | 'pendente'>;
  onOpenTask: (rotina: LstRotinaDoDia) => void;
  compact?: boolean;
  selectedRoutineId?: string | null;
  executingRoutineId?: string | null;
}

export const MeuPadraoPanel = React.memo<MeuPadraoPanelProps>(function MeuPadraoPanel({
  rotinas,
  busyTemplateId,
  statusById,
  onOpenTask,
  compact = true,
  selectedRoutineId = null,
  executingRoutineId = null,
}) {
  const ordered = useMemo(() => [...rotinas].sort((a, b) => meuPadraoSortKey(a) - meuPadraoSortKey(b)), [rotinas]);

  useEffect(() => {
    if (!selectedRoutineId) return;
    const el = document.getElementById(meuPadraoRowId(selectedRoutineId));
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedRoutineId]);

  const handleOpen = useCallback(
    (rotina: LstRotinaDoDia) => onOpenTask(rotina),
    [onOpenTask],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
      <div className="shrink-0 border-b border-slate-100 bg-white/95 px-2.5 py-1 dark:border-slate-800 dark:bg-slate-950/90">
        <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Rotinas do dia</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
      {ordered.map((rotina, idx) => (
        <RotinaRow
          key={rotina.template.id}
          rotina={rotina}
          displayStatus={statusById.get(rotina.template.id) ?? 'pendente'}
          isLast={idx === ordered.length - 1}
          busy={busyTemplateId === rotina.template.id}
          compact={compact}
          highlighted={selectedRoutineId === rotina.template.id}
          isActive={executingRoutineId === rotina.template.id}
          onOpen={handleOpen}
        />
      ))}
      </div>
    </div>
  );
});

interface RotinaRowProps {
  rotina: LstRotinaDoDia;
  displayStatus: LstStatus | 'pendente';
  isLast: boolean;
  busy: boolean;
  compact: boolean;
  highlighted: boolean;
  isActive: boolean;
  onOpen: (rotina: LstRotinaDoDia) => void;
}

const RotinaRow = React.memo<RotinaRowProps>(function RotinaRow({
  rotina,
  displayStatus,
  isLast,
  busy,
  compact,
  highlighted,
  isActive,
  onOpen,
}) {
  const { template, execucao } = rotina;
  const category: LstCategory = template.category ?? 'apoio';
  const isParado = displayStatus === 'processo_parado';
  const isDesvio = displayStatus === 'realizado_com_desvio';
  const isLate = meuPadraoIsLate(rotina, displayStatus);
  const desvioResumo = isDesvio ? formatDesvioJustificativa(execucao?.justificativa_desvio) : null;

  const rowBg = highlighted
    ? 'bg-blue-50/90 ring-2 ring-inset ring-[#2F6FED]/40 dark:bg-blue-950/30'
    : isParado
      ? 'bg-slate-100/70 opacity-90 dark:bg-slate-800/40'
      : isDesvio
        ? 'bg-amber-50/50 dark:bg-amber-950/15'
        : isLate
          ? 'bg-red-50/40 dark:bg-red-950/15'
          : displayStatus === 'realizado'
            ? 'bg-emerald-50/30 dark:bg-emerald-950/10'
            : 'bg-white hover:bg-slate-50/80 dark:bg-slate-900/20 dark:hover:bg-slate-800/30';

  return (
    <button
      id={meuPadraoRowId(template.id)}
      type="button"
      onClick={() => onOpen(rotina)}
      disabled={busy}
      aria-label={`${template.title} — abrir registro`}
      className={`relative flex w-full cursor-pointer items-stretch gap-2 border-l-4 text-left transition-colors ${LST_CATEGORY_STYLES[category].border} ${
        isLast ? '' : 'border-b border-b-slate-100 dark:border-b-slate-800/60'
      } ${rowBg} pl-2 pr-2.5 py-1.5 disabled:opacity-60`}
    >
      <div className="flex w-[44px] shrink-0 flex-col items-center justify-center">
        <span className="text-[12px] font-bold tabular-nums text-slate-700 dark:text-slate-200">
          {formatTimeHHmm(template.scheduled_time) ?? '--:--'}
        </span>
        <span className="text-[8px] font-semibold uppercase tracking-wide text-slate-400">
          {LST_FREQUENCY_LABELS[template.frequency]}
        </span>
      </div>

      <div className="w-px shrink-0 self-stretch bg-slate-200 dark:bg-slate-700/60" aria-hidden />

      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex items-center gap-1.5">
          {template.is_critical && (
            <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" title="Crítica" />
          )}
          <span
            className={`truncate text-[12px] font-semibold ${
              isParado
                ? 'text-slate-500 line-through decoration-slate-400/60'
                : 'text-slate-800 dark:text-slate-100'
            }`}
          >
            {template.title}
          </span>
        </div>
        {desvioResumo && (
          <span className="mt-0.5 line-clamp-1 text-[10px] italic text-amber-700 dark:text-amber-300">
            {desvioResumo}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center self-center">
        <MeuPadraoAgendaBadge
          status={displayStatus}
          rotina={rotina}
          isActive={isActive}
        />
      </div>
    </button>
  );
});

export default MeuPadraoPanel;
